import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import {
  attemptRecoveryReload,
  documentIsStillLoadable,
  isChunkLoadError,
  isUnambiguousChunkLoadError,
  safeSessionStorage,
  shouldAttemptChunkReload,
} from "./chunkErrors.js";
// Direkte imports (IKKE barrel) — saa main-bundlen kun traekker ErrorState +
// Button (+ deres ikon/styles) ind, ikke hele ui-laget (#479). #671 Plan 3.
import ErrorState from "../components/ui/ErrorState.jsx";
import Button from "../components/ui/Button.jsx";
// denyUrls-moenstre i ren .js-fil (unit-testbar uden JSX-import), se #2018.
import { DENY_URLS, isKnownExtensionNoise, isProxiedOrigin, isTranslateProxyHistoryError } from "./sentryDenyUrls.js";
// #5312: fire browser-ordlyd for "kaldet naaede aldrig frem" — ét sted, i en
// ren .js-fil uden sidevirkninger (samme begrundelse som DENY_URLS ovenfor).
import { isBackendUnreachableMessage } from "./backendReachability.js";
// #4595: release-sha'en læses fra <meta name="cz-release"> i HTML'en, IKKE fra
// import.meta.env — en deploy-unik streng i bundlen roterer entry-chunkens hash
// på hvert deploy og gør alle åbne faner stale. Se lib/release.js.
import { getRelease, getSentryRelease } from "./release.js";
import { SENTRY_V10_COMPAT_OPTIONS } from "./sentryCompatOptions.ts";

const DSN = import.meta.env.VITE_SENTRY_DSN;
const ENABLED = import.meta.env.PROD && Boolean(DSN);

// Ét fingerprint for alle chunk-load-fejl, saa de lander i EN gruppe der kan
// arkiveres i Sentry i stedet for at blive slettet i klienten (#4545).
const CHUNK_ERROR_FINGERPRINT = "frontend-chunk-load-error";

// Samme greb for transport-fejl mod backenden (#5312): ét fingerprint, saa de
// fire browser-ordlyd lander i EN gruppe man kan maale og arkivere i Sentry.
// Foer 17/9 blev Firefox-ordlyden ("NetworkError when attempting to fetch
// resource") droppet helt herunder — omfanget kunne derfor ikke ses, og
// foerste signal paa haendelsen 16/9 var en Discord-besked fra en spiller,
// ikke dashboardet. Praecis samme laering som #4545 om chunk-fejl.
const NETWORK_ERROR_FINGERPRINT = "frontend-backend-network-error";

let started = false;

// #4595: env-variablen slås op med en STATISK `import.meta.env.VITE_*`-reference,
// aldrig med `import.meta.env[name]`. Dynamisk indeksering tvinger Vite til at
// inline HELE env-objektet — inklusive Vercels auto-eksponerede
// `VITE_VERCEL_GIT_COMMIT_SHA` — så sha'en endte i bundlen igen ad bagdøren, selv
// efter at de to direkte læsninger var fjernet. Målt: 3 forekomster af sha'en i
// entry-chunken i et build med DSN sat (altså prod), 0 uden.
function toSampleRate(raw, fallback = 0) {
  // CodeRabbit (#4970): tom streng, ikke-numeriske vaerdier og tal udenfor det
  // inklusive interval [0, 1] skal falde tilbage til `fallback` — @sentry/react
  // kraever et tal i [0, 1] og afviser (eller warner paa) alt andet. Tjek "" eksplicit
  // FOER Number(), da Number("") === 0 ellers ville maskere en tom env-vaerdi som
  // et gyldigt 0 i stedet for at bruge fallback.
  if (raw === undefined || raw === null || raw === "") return fallback;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : fallback;
}

export function initSentry() {
  if (started || !ENABLED) return;
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: getSentryRelease(),
    tracesSampleRate: toSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    replaysSessionSampleRate: toSampleRate(import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE),
    replaysOnErrorSampleRate: toSampleRate(import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE, 0.1),
    ...SENTRY_V10_COMPAT_OPTIONS, // #6607: v10-privatlivs-baseline under Sentry 11
    // #1792 (extensions) + #2018 (Vercel Live Feedback toolbar): dropper events
    // hvis "blame"-frame stammer fra tredjeparts-injiceret kode. Se DENY_URLS.
    denyUrls: DENY_URLS,
    beforeSend(event) {
      const value = event.exception?.values?.[0]?.value || event.message || "";
      const errorType = event.exception?.values?.[0]?.type || "";
      // #5694 (CYCLINGZONE-68): siden kører under Google Translate-proxyens
      // eget origin (fx cyclingzone-org.translate.goog) når en spiller bruger
      // browser-oversættelse, og proxyen omskriver document-URL'en uden at
      // document.origin følger med — det udløser en SecurityError fra
      // history.replaceState/pushState, som ikke er vores app der fejler.
      //
      // CodeRabbit-fund (24/9): et tidligere udkast droppede ALLE events fra
      // .translate.goog uanset fejltype — det ville også have skjult ægte
      // app-crashes for de spillere. Tjekket er derfor snævret ind til
      // PRÆCIS den identificerede fejl (origin OG fejltype/besked), ikke hele
      // origin'et.
      if (typeof window !== "undefined" && isProxiedOrigin(window.location?.hostname) && isTranslateProxyHistoryError(errorType, value)) {
        return null;
      }
      if (/ResizeObserver loop completed/i.test(value)) {
        return null;
      }
      // #4499: erstatter den tidligere URL-baserede webkit-masked-url-regel i
      // denyUrls (fjernet — den droppede ALLE WebKit-modul-fejl, ikke kun
      // extension-stoej, se sentryDenyUrls.js). Matcher paa selve beskeden,
      // som WebKit ikke maskerer.
      if (isKnownExtensionNoise(value)) {
        return null;
      }
      // #4545: chunk-fejl blev FOER droppet helt (#881: "recoverable stoej,
      // deploy-sundhed overvaages via Vercel"). Det kostede to ting paa én gang:
      // fejl-id'et fallbacken viser spilleren pegede paa et event der aldrig blev
      // sendt, og omfanget var umaaleligt — foerste signal paa haendelsen 1/9 var
      // en Discord-besked fra en spiller, ikke dashboardet.
      //
      // De sendes nu som warning under ÉT fingerprint. Saa bliver de én gruppe man
      // kan arkivere i Sentry: stoejen styres der hvor man kan se hvad man slaar
      // fra, i stedet for her hvor man ikke kan.
      //
      // KUN de utvetydige moenstre grupperes. isChunkLoadError() matcher ogsaa
      // React.lazy-interne strenge som almindelig kode kan producere (se
      // chunkErrors.js) — at samle dem her ville begrave et aegte crash i en
      // daempet chunk-gruppe, hvilket er praecis den fejl vi retter.
      if (event.tags?.frontend_error_kind === "chunk_load_error" || isUnambiguousChunkLoadError({ message: value })) {
        event.level = "warning";
        event.fingerprint = [CHUNK_ERROR_FINGERPRINT];
      } else if (isBackendUnreachableMessage(value)) {
        // #5312: "naaede aldrig serveren" er ikke en kodefejl — men den skal
        // KUNNE TAELLES. Warning (ikke error) holder den ude af alarmerne;
        // det faste fingerprint goer den til én gruppe i stedet for én pr.
        // side, saa man kan se om det rammer 3 spillere eller 30.
        // Rækkefølgen er bevidst: chunk-grenen vinder, fordi en fejlet
        // modul-load ogsaa hedder "Failed to fetch".
        event.level = "warning";
        event.fingerprint = [NETWORK_ERROR_FINGERPRINT];
        event.tags = { ...event.tags, frontend_error_kind: "network_error" };
      }
      return event;
    },
  });
  started = true;
}

export function classifyFrontendError(error) {
  if (isUnambiguousChunkLoadError(error)) return "chunk_load_error";
  if (isChunkLoadError(error)) return "possible_chunk_load_error";
  return "render_error";
}

export function SentryBoundary({ children }) {
  // Altid-aktiv: Sentry.ErrorBoundary fungerer som en almindelig React-
  // error-boundary selv uden init (captureException er en no-op uden client),
  // saa render-fejl fanges OGSAA i dev/preview/uden DSN -> branded fallback i
  // stedet for white-screen (#671 Plan 3). Rapportering sker kun naar ENABLED.
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope, error) => {
        // Tre vaerdier, ikke to (#4545): "possible_chunk_load_error" er de
        // React.lazy-interne signaturer som almindelig kode ogsaa kan producere.
        // Recovery behandler dem stadig som chunk-fejl (billigt at tage fejl),
        // men de daempes ikke i Sentry — saa et aegte crash forbliver synligt,
        // og du kan maale hvor stor den tvetydige bunke faktisk er.
        scope.setTag("frontend_error_kind", classifyFrontendError(error));
        const release = getSentryRelease();
        if (release) scope.setTag("frontend_release", release);
      }}
      fallback={(props) => <AppErrorFallback {...props} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

// AnalyticsBoundary — tavs, isoleret boundary omkring de client-only
// analytics-/beacon-integrationer (Clarity, WebVitals, Vercel Analytics, GA,
// TrafficBeacon). CYCLINGZONE-5B (4/9): et deploy-skew gav ClarityIntegration en
// ANDEN modul-instans af consent.jsx end provider-traeet, saa useConsent() fandt
// en tom context og kastede. Fordi analytics-blokken laa direkte under
// SentryBoundary, tog den HELE appen ned i fuldskaerms-fallbacken for 4 spillere
// — og fejlen klassificeres som "render_error", saa chunk-selvhelingen (#4595)
// greb den ikke: brugeren sad fast indtil manuel reload.
//
// Analytics er per definition ikke-kritisk. En fejl der ALDRIG maa koste
// spilleren adgang til spillet hoerer hjemme bag sin egen boundary: vi rapporterer
// den (saa den forbliver synlig i Sentry med sit eget tag) og renderer null.
// Rod-aarsagen — deploy-skew, #2423 — loeses ikke her; det her sikrer bare at
// naeste skew-vindue koster telemetri i stedet for hele appen.
export function AnalyticsBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope, error) => {
        scope.setTag("frontend_error_kind", classifyFrontendError(error));
        scope.setTag("frontend_error_scope", "analytics");
        const release = getSentryRelease();
        if (release) scope.setTag("frontend_release", release);
      }}
      fallback={null}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

// #1170 slice B-beslutning: boundary-copy herunder er BEVIDST statisk (ingen
// t()/i18n). Error-boundary kan ramme før i18n er initialiseret eller mens et
// chunk-load fejler, så den må ikke afhænge af i18n-runtime. EN er default;
// DA vælges kun ved eksplicit cz_lang=da (samme nøgle som LanguageProvider)
// eller dansk browser-sprog. Filen er EXEMPT i scripts/i18n-check-lib-strings.mjs.
function getPreferredLanguage() {
  if (typeof window === "undefined") return "en";
  try {
    return window.localStorage?.getItem("cz_lang") || window.navigator?.language || "en";
  } catch {
    return window.navigator?.language || "en";
  }
}

function AppErrorFallback({ error, eventId, resetError }) {
  const chunkError = isChunkLoadError(error);
  // Sat naar auto-recovery er opbrugt for denne release — se effecten nedenfor.
  const [recoveryExhausted, setRecoveryExhausted] = useState(false);
  // "Stuck" = chunk-fejl hvor der IKKE kommer et automatisk reload. Copyen skal
  // sige hvad der faktisk sker, og give spilleren det skridt der virker: et
  // haardt gen-indlaes, og ellers rydning af sidens data. Et almindeligt reload
  // henter ikke en `immutable`-cachet ressource igen, saa knappen ovenfor kan
  // ikke loese det (#4545).
  const stuck = chunkError && recoveryExhausted;
  const lang = getPreferredLanguage().toLowerCase().startsWith("da") ? "da" : "en";
  const copy = lang === "da"
    ? {
        title: stuck
          ? "Siden kunne ikke indlæses"
          : chunkError ? "Cycling Zone er opdateret" : "Siden kunne ikke vises",
        body: stuck
          ? "Genindlæsning løste det ikke. Et hårdt gen-indlæs plejer at virke: hold Shift nede, og klik Genindlæs. Sker det stadig, så ryd sidens gemte data i browserens indstillinger."
          : chunkError
            ? "Din browser havde en ældre version af siden åben. Vi prøver at genindlæse den nye version automatisk."
            : "Der skete en fejl i appen. Fejlen er registreret, og du kan prøve at genindlæse siden.",
        reload: "Genindlæs siden",
        retry: "Prøv igen",
        event: "Fejl-id",
      }
    : {
        title: stuck
          ? "The page could not be loaded"
          : chunkError ? "Cycling Zone was updated" : "The page could not be shown",
        body: stuck
          ? "Reloading did not fix it. A hard refresh usually does: hold Shift and click Reload. If it keeps happening, clear this site's saved data in your browser settings."
          : chunkError
            ? "Your browser had an older version of the page open. We are trying to reload the new version automatically."
            : "The app hit an error. The error has been recorded, and you can try reloading the page.",
        reload: "Reload page",
        retry: "Try again",
        event: "Error ID",
      };

  // Auto-recovery ved stale chunk (#881/#906) — navigations-guarded (#3602).
  //
  // Denne effect kaldte før `window.location.reload()` synkront. Men WebKit melder
  // en chunk-load der blev ABORTERET af en igangværende navigation med præcis
  // samme fejlstreng som en ægte stale chunk, så boundary'en reloadede dokumenter
  // der allerede var på vej væk — og kaprede brugerens navigation (målt: reload
  // 39 ms efter aborten, 1,4 s før den ægte navigation nåede at committe).
  //
  // documentIsStillLoadable() spørger dokumentet om det stadig kan hente noget.
  // Kun hvis ja brænder vi loop-guard-nøglen og reloader. Se chunkErrors.js.
  //
  // #5159 (review-fund 1): stien går nu gennem den SAMME `attemptRecoveryReload`
  // som den globale preload-handler, og dermed gennem porten (reloadGate.js).
  // Fallbacken herunder ER den manuelle udvej — den har sin egen "Genindlæs
  // siden"-knap — så er porten lukket, står den og venter i stedet for at rive
  // dokumentet væk. Vi vækkes igen når den sidste blokering slippes.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Gate på chunk-fejl FØR canary'en: en almindelig render-fejl skal ikke
    // udløse et netværkskald.
    if (!isChunkLoadError(error)) return undefined;

    let cancelled = false;
    const cancel = () => { cancelled = true; };
    window.addEventListener("pagehide", cancel);

    attemptRecoveryReload({
      probe: () => documentIsStillLoadable({
        fetchFn: window.fetch?.bind(window),
        url: window.location.href,
      }),
      // #5159 (M3/review-fund 4): den sikre accessor, ikke en rå property-
      // læsning. `window.sessionStorage` KASTER i browsere hvor site-data er
      // slået fra, og et kast her ville blive til en ny fejl inde i selve
      // fejlskærmen.
      claim: () => shouldAttemptChunkReload({
        error,
        release: getRelease(),
        storage: safeSessionStorage(window),
      }),
      reload: () => window.location.reload(),
      source: "boundary",
      isCancelled: () => cancelled,
    }).then((outcome) => {
      if (cancelled || outcome !== "exhausted") return;
      // #4545: dokumentet ER i live, men loop-guarden er braendt — ét reload er
      // allerede brugt paa denne release. Der sker altsaa INTET automatisk herfra,
      // og copyen maa ikke blive ved med at love det. Uden dette flag sad
      // spilleren 1/9 i en loekke: samme "vi genindlaeser automatisk", nyt fejl-id,
      // i det uendelige, uden at faa at vide hvad han selv kunne goere.
      setRecoveryExhausted(true);
    }).catch(() => {});

    return () => {
      cancel();
      window.removeEventListener("pagehide", cancel);
    };
  }, [error]);

  // On-spec branded fallback paa ErrorState + Button (#671 Plan 3). Statisk
  // copy bevaret (#1170); eventId vises kun naar ENABLED (deterministisk +
  // meningsfuldt — vi viser kun et id vi faktisk har rapporteret).
  return (
    // #5325: role="alert" sidder på selve ErrorState (kanonisk komponent) -
    // en ydre wrapper her ville give to alert-noder i DOM'en for samme fejl.
    <main className="flex min-h-screen items-center justify-center bg-cz-body px-4 py-10 text-cz-1">
      <ErrorState
        className="w-full max-w-lg"
        title={copy.title}
        description={copy.body}
        action={
          <div className="flex flex-col items-center gap-3">
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
                {copy.reload}
              </Button>
              {/* #4545: "Proev igen" skjules mens et automatisk reload er paa vej,
                  men vises igen naar recovery er opbrugt — saa spilleren har en
                  udvej ud over den knap der beviseligt ikke hjalp ham. */}
              {(!chunkError || stuck) && (
                <Button variant="secondary" size="sm" onClick={() => resetError?.()}>
                  {copy.retry}
                </Button>
              )}
            </div>
            {ENABLED && eventId && (
              <p className="font-mono text-2xs text-cz-3">
                {copy.event}: {eventId}
              </p>
            )}
          </div>
        }
      />
    </main>
  );
}

// User-context helpers (#621 item 2). Tag hver event med user.id så Sentry
// "Affected users"-counter virker. KUN UUID — ingen email, ingen team-navn,
// ingen PII (GDPR-safe).
export function setSentryUser(userId) {
  if (!ENABLED || !userId) return;
  Sentry.setUser({ id: userId });
}

export function clearSentryUser() {
  if (!ENABLED) return;
  Sentry.setUser(null);
}
